/**
 * App store routing helpers (Feb 2026).
 *
 * AkountFly is live on both stores after Apple approval:
 *   iOS:     https://apps.apple.com/ng/app/akountfly/id6800300478
 *   Android: https://play.google.com/store/apps/details?id=com.akountfly.app
 *
 * URLs come from env so we can override per-environment without a rebuild.
 * We fall back to hard-coded prod URLs if the env vars are missing so
 * previews and tests keep working.
 */

import { isNativeApp } from './nativeBridge';

export const IOS_APP_STORE_URL =
  process.env.REACT_APP_IOS_APP_STORE_URL ||
  'https://apps.apple.com/ng/app/akountfly/id6800300478';

export const ANDROID_PLAY_STORE_URL =
  process.env.REACT_APP_ANDROID_PLAY_STORE_URL ||
  'https://play.google.com/store/apps/details?id=com.akountfly.app';

/** Detect the mobile platform of the current browser. Returns 'ios',
 *  'android' or 'desktop'. Safe on SSR / no-window contexts. */
export const detectMobilePlatform = () => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  // iPadOS 13+ pretends to be Mac. If touch is available and it says Mac -> treat as iOS.
  if (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
};

/** Store URL for the current device. Desktop callers should pick a
 *  specific store explicitly — this helper is for "get the app on THIS
 *  device" flows (banner, setup-complete). */
export const getStoreUrlForCurrentDevice = () => {
  const p = detectMobilePlatform();
  if (p === 'ios') return IOS_APP_STORE_URL;
  if (p === 'android') return ANDROID_PLAY_STORE_URL;
  // Desktop fallback: hand them the Play Store because it renders a
  // browser-friendly listing (App Store on desktop is 1-click iTunes).
  return ANDROID_PLAY_STORE_URL;
};

/** True when we should show "download the app" UI (mobile web only). */
export const shouldOfferAppDownload = () => {
  if (isNativeApp()) return false; // already in the app
  return detectMobilePlatform() !== 'desktop';
};

/** Universal Link that opens the AkountFly app when installed (via
 *  iOS Universal Links / Android App Links) and otherwise loads in the
 *  browser. AASA / assetlinks are served from akountfly.com/.well-known/*.
 *
 *  With a one-time authorization `code` we point at /auth/callback (claimed
 *  by AASA) so the PWA inside the app shell exchanges it for a session.
 *  Without a code we point at /dashboard rather than the marketing home so
 *  the OS still hands off to the app. Bare akountfly.com is NOT claimed. */
export const buildAppUniversalLink = (code) => {
  if (code) return `https://akountfly.com/auth/callback?code=${encodeURIComponent(code)}`;
  return 'https://akountfly.com/dashboard';
};

/** Custom scheme deep link — kept for legacy native-wrapper handoff only.
 *  Prefer buildAppUniversalLink for web-initiated flows so the OS can
 *  decide app-vs-browser cleanly. */
export const buildAppDeepLink = (code) =>
  code
    ? `akountfly:///auth/callback?code=${encodeURIComponent(code)}`
    : 'akountfly://dashboard';

/** Android intent:// link: opens the installed app, otherwise Chrome
 *  takes the user straight to the Play Store (S.browser_fallback_url).
 *  Deterministic — no timers. */
export const buildAndroidIntentLink = (code) => {
  const path = code ? `auth/callback?code=${encodeURIComponent(code)}` : 'dashboard';
  return `intent://${path}#Intent;scheme=akountfly;package=com.akountfly.app;S.browser_fallback_url=${encodeURIComponent(ANDROID_PLAY_STORE_URL)};end`;
};

/** Oct 2026 · Open the INSTALLED app from a web page on the same domain.
 *  Universal/App Links never fire for same-domain navigation initiated
 *  from akountfly.com itself (iOS and Android both stay in the browser),
 *  so we use the custom scheme on iOS and an intent:// link on Android. */
export const buildOpenInstalledAppLink = (code) =>
  detectMobilePlatform() === 'android' ? buildAndroidIntentLink(code) : buildAppDeepLink(code);

/** Single-tap "Open AkountFly App": fires the installed-app link and, if the
 *  page is still visible ~1.5s later (app not installed), sends the user to
 *  this device's store. Android intent links already carry their own store
 *  fallback; the timer is a safety net for browsers that ignore intent://. */
export const openInstalledAppOrStore = (code) => {
  if (typeof window === 'undefined') return;
  const storeUrl = getStoreUrlForCurrentDevice();
  let handedOff = false;
  const markHidden = () => { if (document.visibilityState === 'hidden') handedOff = true; };
  const markPageHide = () => { handedOff = true; };
  document.addEventListener('visibilitychange', markHidden);
  window.addEventListener('pagehide', markPageHide);
  window.location.href = buildOpenInstalledAppLink(code);
  setTimeout(() => {
    document.removeEventListener('visibilitychange', markHidden);
    window.removeEventListener('pagehide', markPageHide);
    if (!handedOff && document.visibilityState === 'visible') window.location.href = storeUrl;
  }, 1500);
};

/** Try to open the AkountFly app via Universal Link. If the app isn't
 *  installed the browser stays put (Universal Links fall through
 *  transparently) and we fall back to the store after ~1.6s if the tab
 *  is still visible. This is safer than firing the custom scheme first
 *  because iOS shows an ugly "cannot open" prompt for unknown schemes.
 */
export const openAppOrStore = ({ code, onFallback } = {}) => {
  if (typeof window === 'undefined') return null;
  const storeUrl = getStoreUrlForCurrentDevice();
  const universalLink = buildAppUniversalLink(code);

  const startedAt = Date.now();
  let fallbackTimer = null;

  const goToStore = () => {
    if (typeof onFallback === 'function') {
      try { onFallback(storeUrl); } catch { /* noop */ }
    }
    window.location.href = storeUrl;
  };

  // If the tab hides quickly, the OS handed off to the app — bail out
  // of the store fallback.
  const onVisibility = () => {
    if (document.visibilityState === 'hidden' && Date.now() - startedAt < 1800) {
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  // Best-effort Universal Link launch. iOS >= 9 / Android >= 6 will
  // hand off to the app if it is installed AND has claimed the path in
  // AASA / assetlinks. Otherwise the browser navigates normally, which
  // we detect via the visibility check + timer.
  try {
    window.location.href = universalLink;
  } catch { /* noop */ }

  fallbackTimer = setTimeout(() => {
    document.removeEventListener('visibilitychange', onVisibility);
    if (document.visibilityState === 'visible') goToStore();
  }, 1600);

  return fallbackTimer;
};
